import { useState } from "react";
import { useCustom } from "@refinedev/core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RefreshCw,
  Eye,
  Clock,
} from "lucide-react";
import type { TriggerLogEntry } from "./log-types";
import { getTriggerLogResultConfig } from "./log-view";

interface TriggerLogsProps {
  triggerId: string;
  triggerName: string;
}

/**
 * TriggerLogs - компонент логов выполнения триггеров
 *
 * Отображает таблицу с логами выполнения триггеров.
 * Поддерживает просмотр деталей, фильтрацию и пагинацию.
 *
 * @param props - Пропсы компонента
 * @param props.triggerId - ID триггера для фильтрации логов
 * @param props.triggerName - Название триггера для отображения в заголовке
 * @returns {React.ReactElement} Компонент с таблицей логов
 */
export function TriggerLogs({ triggerId, triggerName }: TriggerLogsProps) {
  const [selectedLog, setSelectedLog] = useState<TriggerLogEntry | null>(null);

  const { result, query } = useCustom<TriggerLogEntry[]>({
    url: `/admin/triggers/${triggerId}/logs?limit=50`,
    method: 'get',
  });

  const logs = Array.isArray(result?.data) ? result.data : [];
  const isLoading = query.isLoading;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Логи выполнения</CardTitle>
          <p className="text-sm text-muted-foreground">{triggerName}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => query.refetch()}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
          />
          Обновить
        </Button>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {isLoading ? "Загрузка..." : "Нет записей о выполнении"}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Дата</TableHead>
                <TableHead>Результат</TableHead>
                <TableHead>Пользователь</TableHead>
                <TableHead>Сообщение</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const config = getTriggerLogResultConfig(log);
                const Icon = config.icon;
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {formatDate(log.executedAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={config.className}>
                        <Icon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {log.userId ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {log.error ? (
                        <span className="text-destructive text-sm truncate max-w-[200px] block">
                          {log.error}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedLog(log)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Detail Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Детали выполнения</DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Дата
                    </label>
                    <p>{formatDate(selectedLog.executedAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Пользователь
                    </label>
                    <p>{selectedLog.userId ?? "—"}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Результат
                  </label>
                  <Badge className={getTriggerLogResultConfig(selectedLog).className}>
                    {getTriggerLogResultConfig(selectedLog).label}
                  </Badge>
                </div>

                {selectedLog.error && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Ошибка
                    </label>
                    <pre className="bg-destructive/10 text-destructive p-3 rounded text-sm overflow-x-auto">
                      {selectedLog.error}
                    </pre>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Входные данные
                  </label>
                  <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.input, null, 2)}
                  </pre>
                </div>

                {selectedLog.output !== null && selectedLog.output !== undefined && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Результат выполнения
                    </label>
                    <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
                      {JSON.stringify(selectedLog.output, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
